import React, { useState, useEffect } from 'react';
import { X, Search, Clock, AlertCircle, CheckSquare, Square, Loader2, FileText, CheckCircle2, ShieldCheck } from 'lucide-react';
import { getCustomerPendingInvoices } from '../../../../services/api/customerApi';

const PendingInvoicesModal = ({ customerId, isOpen, onClose, onAddSelected, alreadySelectedInvoiceNos = [], excludeInvoiceNos = [] }) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMap, setSelectedMap] = useState({});

    useEffect(() => {
        if (isOpen && customerId) {
            fetchPendingInvoices();
        } else {
            setInvoices([]);
            setSelectedMap({});
            setSearchQuery('');
            setError(null);
        }
    }, [isOpen, customerId]);

    const fetchPendingInvoices = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getCustomerPendingInvoices(customerId);
            const rawList = Array.isArray(data) ? data : [];
            const cleanExclusions = excludeInvoiceNos.map(no => (no || '').replace(/^#/, '').trim().toUpperCase());
            const list = rawList.filter(inv => {
                const cleanInvNo = (inv.invoiceNo || '').replace(/^#/, '').trim().toUpperCase();
                return !cleanExclusions.includes(cleanInvNo);
            });
            setInvoices(list);

            // Pre-select already added invoices
            const initialMap = {};
            list.forEach(inv => {
                if (alreadySelectedInvoiceNos.includes(inv.invoiceNo)) {
                    initialMap[inv.invoiceNo] = true;
                }
            });
            setSelectedMap(initialMap);
        } catch (err) {
            console.error('Failed to fetch pending invoices:', err);
            setError(err?.response?.data?.message || err.message || 'Failed to load customer pending invoices');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const filteredInvoices = invoices.filter(inv => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase().trim();
        return (
            (inv.invoiceNo && inv.invoiceNo.toLowerCase().includes(q)) ||
            (inv.invoiceDate && inv.invoiceDate.includes(q)) ||
            (inv.outstandingAmount && String(inv.outstandingAmount).includes(q))
        );
    });

    const toggleSelectAll = () => {
        const allSelected = filteredInvoices.every(inv => selectedMap[inv.invoiceNo]);
        const newMap = { ...selectedMap };
        filteredInvoices.forEach(inv => {
            newMap[inv.invoiceNo] = !allSelected;
        });
        setSelectedMap(newMap);
    };

    const toggleSelect = (invoiceNo) => {
        setSelectedMap(prev => ({
            ...prev,
            [invoiceNo]: !prev[invoiceNo]
        }));
    };

    const handleConfirm = () => {
        const selectedList = invoices.filter(inv => selectedMap[inv.invoiceNo]);
        onAddSelected(selectedList);
        onClose();
    };

    const selectedCount = Object.values(selectedMap).filter(Boolean).length;

    const getAgingBadge = (category, dueDays) => {
        if (category === 'GREEN' || dueDays <= 7) {
            return (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    🟢 {dueDays} {dueDays === 1 ? 'day' : 'days'} overdue
                </span>
            );
        }
        if (category === 'YELLOW' || dueDays <= 30) {
            return (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200/60">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    🟡 {dueDays} days overdue
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200/60">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                🔴 {dueDays} days overdue
            </span>
        );
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    return (
        <div className="fixed inset-0 z-[350] flex items-center justify-center p-3 sm:p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-3xl overflow-hidden max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between p-4 sm:p-6 border-b border-gray-100 gap-3 shrink-0 bg-slate-50/50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-sm shrink-0">
                            <Clock size={24} />
                        </div>
                        <div>
                            <h2 className="text-base sm:text-xl font-bold text-gray-900 leading-tight">
                                Select Pending Invoices
                            </h2>
                            <p className="text-xs font-medium text-gray-500 mt-0.5">
                                Prioritize collecting older dues first (Color-coded by overdue days)
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 transition-colors shrink-0"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body Content */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
                    {/* Search & Actions Bar */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by invoice number or date..."
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                            />
                        </div>
                        {filteredInvoices.length > 0 && (
                            <button
                                type="button"
                                onClick={toggleSelectAll}
                                className="px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-xs font-bold text-gray-700 flex items-center justify-center gap-2 transition-colors shrink-0"
                            >
                                {filteredInvoices.every(inv => selectedMap[inv.invoiceNo]) ? (
                                    <>
                                        <CheckSquare size={16} className="text-blue-600" /> Unselect All
                                    </>
                                ) : (
                                    <>
                                        <Square size={16} className="text-gray-400" /> Select All
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    {/* Error Banner */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-xs font-bold flex items-center gap-2">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Loading State */}
                    {loading ? (
                        <div className="py-16 flex flex-col items-center justify-center gap-3 text-gray-400">
                            <Loader2 size={32} className="animate-spin text-blue-600" />
                            <p className="text-xs font-semibold">Fetching customer pending invoices...</p>
                        </div>
                    ) : filteredInvoices.length === 0 ? (
                        /* Empty / All Clear Indication State */
                        <div className="py-12 flex flex-col items-center justify-center text-center p-6 bg-emerald-50/40 rounded-2xl border border-emerald-200/80">
                            <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3 shadow-sm">
                                <ShieldCheck size={32} />
                            </div>
                            <h3 className="text-base font-black text-emerald-900 flex items-center gap-2">
                                🟢 No Active Pending Invoices
                            </h3>
                            <p className="text-xs font-medium text-emerald-700 mt-1 max-w-sm">
                                {searchQuery ? 'No pending invoices match your search query.' : 'All previous sales for this customer have been fully settled. There are no outstanding balances to collect.'}
                            </p>
                        </div>
                    ) : (
                        /* Invoices List / Table */
                        <div className="border border-gray-200/80 rounded-2xl overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-gray-100/70 border-b border-gray-200 text-gray-600 font-bold uppercase tracking-wider">
                                        <tr>
                                            <th className="py-3 px-4 w-12 text-center">Select</th>
                                            <th className="py-3 px-4">Invoice</th>
                                            <th className="py-3 px-4">Date</th>
                                            <th className="py-3 px-4 text-right">Outstanding</th>
                                            <th className="py-3 px-4 text-center">Due Days & Aging</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {filteredInvoices.map((inv) => {
                                            const isSelected = Boolean(selectedMap[inv.invoiceNo]);
                                            return (
                                                <tr
                                                    key={inv.invoiceNo}
                                                    onClick={() => toggleSelect(inv.invoiceNo)}
                                                    className={`cursor-pointer transition-colors hover:bg-blue-50/40 ${isSelected ? 'bg-blue-50/70' : ''}`}
                                                >
                                                    <td className="py-3.5 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleSelect(inv.invoiceNo)}
                                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </td>
                                                    <td className="py-3.5 px-4 font-black text-gray-900">
                                                        <div className="flex items-center gap-2">
                                                            <FileText size={15} className="text-gray-400 shrink-0" />
                                                            <span>{inv.invoiceNo}</span>
                                                            {inv.status === 'PARTIALLY_PAID' && (
                                                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                                                    Partial
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-3.5 px-4 font-semibold text-gray-600">
                                                        {formatDate(inv.invoiceDate)}
                                                    </td>
                                                    <td className="py-3.5 px-4 text-right font-black text-emerald-700 text-sm">
                                                        AED {Number(inv.outstandingAmount || 0).toFixed(2)}
                                                    </td>
                                                    <td className="py-3.5 px-4 text-center">
                                                        {getAgingBadge(inv.agingCategory, inv.dueDays)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Action Bar */}
                <div className="p-4 sm:p-6 border-t border-gray-100 bg-gray-50/60 flex items-center justify-between gap-3 shrink-0">
                    <div className="text-xs font-semibold text-gray-500">
                        {selectedCount > 0 ? (
                            <span className="text-blue-600 font-bold flex items-center gap-1.5">
                                <CheckCircle2 size={16} /> {selectedCount} {selectedCount === 1 ? 'invoice' : 'invoices'} selected
                            </span>
                        ) : (
                            'Select invoices to add to Record Sale'
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-100 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={selectedCount === 0}
                            className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-md shadow-blue-200 transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50 disabled:scale-100"
                        >
                            Add Selected ({selectedCount})
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PendingInvoicesModal;
